import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { AvatarUpload, DataTable, IconAvatar, StatusBadge } from "../../components/Common/PageWidgets";
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import SecondaryButton from "../../components/Common/SecondaryButton";
import ActivityDetailModal from "../../components/Common/ActivityDetailModal";
import EmptyState from "../../components/Common/EmptyState";


//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";


import whiteArrow from "../../assets/images/user-dashboard/white-right-arrow.svg";
import eyeIcon from "../../assets/images/support/eye.svg";
import coinIcon from "../../assets/images/user-dashboard/voucher.svg";
import ticketIcon from "../../assets/images/user-dashboard/redeem.svg";
import starIcon from "../../assets/images/user-dashboard/star.svg";
import thumbPlaceholder from "../../assets/images/user-dashboard/thumb-placeholder.svg";

/* -------------------------------------------------------------------------
 * ProfilePage — read-only profile summary + activity history.
 * ---------------------------------------------------------------------- */

const ACTIVITY_ICON = {
  referral_referrer: { icon: starIcon, tone: "teal" },
  referral_referee: { icon: starIcon, tone: "teal" },
  voucher_redemption: { icon: ticketIcon, tone: "orange" },
  manual_adjustment: { icon: coinIcon, tone: "blue" },
  passport: { icon: starIcon, tone: "purple" },
  streak: { icon: starIcon, tone: "teal" },
  game: { icon: starIcon, tone: "orange" },
};

const LEDGER_TYPE_LABELS = {
  transaction: "Transaction",
  referral_referrer: "Referral Bonus",
  referral_referee: "Referral Bonus",
  voucher_redemption: "Voucher Redemption",
  manual_adjustment: "Manual Adjustment",
  passport: "Passport Reward",
  streak: "Streak Reward",
  game: "Game Reward",
};

const SOURCE_TYPE_LABELS = {
  "App\\Models\\FinancialTransaction": "Financial Transaction",
  financial_transaction: "Financial Transaction",
  transaction: "Financial Transaction",
  voucher: "Voucher",
  "App\\Models\\Voucher": "Voucher",
  referral: "Referral",
  admin_manual_adjustment: "Admin Manual Adjustment",
  "App\\Models\\UserGameProgress": "Game Progress",
  game: "Game Progress",
  "App\\Models\\UserStreak": "Streak",
  streak: "Streak",
  "App\\Models\\UserPassportReward": "Passport",
  passport: "Passport",
};

const toTitleCase = (value) => (
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatSourceType = (value) => {
  if (!value) return "-";
  if (SOURCE_TYPE_LABELS[value]) return SOURCE_TYPE_LABELS[value];

  const className = String(value).split("\\").pop();
  return SOURCE_TYPE_LABELS[className] || toTitleCase(className);
};

const formatDateTime = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${datePart} ${timePart}`;
};

const activityColumns = (onView) => [
  {
    key: "type",
    label: "Name",
    width: "2fr",
    render: (row) => {
      const { icon, tone } = ACTIVITY_ICON[row.type] || ACTIVITY_ICON.manual_adjustment;
      return (
        <span className="data-table__row-label">
          <IconAvatar icon={icon} tone={tone} size={32} />
          {LEDGER_TYPE_LABELS[row.type] || toTitleCase(row.type)}
        </span>
      );
    },
  },
  {
    key: "source_type",
    label: "Type",
    width: "1fr",
    render: (row) => row.sourceTypeLabel,
  },
  {
    key: "status",
    label: "Status",
    width: "1fr",
    render: (row) => <StatusBadge status={row.statusLabel} />,
  },
  {
    key: "points",
    label: "Points",
    width: "1fr",
    render: (row) => (
      <span style={{ color: row.status === 'credit' ? '#1D9C87' : '#d11f1f', fontWeight: 600 }}>
        {row.pointsDisplay}
      </span>
    ),
  },
  {
    key: "dateLabel",
    label: "Date",
    width: "1fr",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView && onView(row.id)}
        className="view-activity-btn"
        aria-label={`View ${row.typeLabel || "activity"}`}
      >
        <img src={eyeIcon} alt="" className="view-activity-btn__icon" />
      </button>
    ),
  },
];

const ProfilePage = ({ profile, activity, onOpenSettings, onViewActivity }) => {
  document.title = "Profile | Omaha Food Lovers";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch user profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.me);
      
      if (response.data.success) {
        setUserProfile(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch transactions from API
  const fetchTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const response = await get(`${endpoints.pointsLedger}?page=1`);
      
      if (response.data.success) {
        const transactionsData = response.data.data;
        const newTransactions = transactionsData.data || [];
        
        // Transform API response to match component's expected format
        const transformedTransactions = newTransactions.map((tx) => {
          const status = tx.status === "debit" ? "debit" : "credit";
          const points = Math.abs(Number(tx.points || 0));
          const sourceTypeLabel = formatSourceType(tx.source_type);

          return {
            id: tx.id,
            type: tx.type,
            typeLabel: LEDGER_TYPE_LABELS[tx.type] || toTitleCase(tx.type),
            status,
            statusLabel: status === "credit" ? "Credit" : "Debit",
            source_type: tx.source_type,
            sourceTypeLabel,
            points,
            pointsDisplay: `${status === "credit" ? "+" : "-"}${points.toLocaleString()}`,
            dateLabel: formatDateTime(tx.created_at),
            created_at: tx.created_at,
            meta: tx.meta,
          };
        });
        
        setTransactions(transformedTransactions);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  // Fetch transactions on mount
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleViewActivity = (activityId) => {
    const act = transactions.find(a => a.id === activityId);
    if (act) {
      const { icon } = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.manual_adjustment;
      const modalActivityData = {
        icon,
        statusLabel: act.statusLabel,
        title: act.typeLabel,
        description: act.sourceTypeLabel,
        type: act.sourceTypeLabel,
        event_label: act.statusLabel,
        date: act.dateLabel,
        points: act.status === 'debit' ? -act.points : act.points,
        pointsTone: act.status === 'credit' ? 'green' : 'red',
      };
      setSelectedActivity(modalActivityData);
      setIsModalOpen(true);
    }
    if (onViewActivity) onViewActivity(activityId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <div className="page-content profile-page">
      <Container fluid>
        <div className="surface-card profile-top-card">
          <div className="profile-header">
            <h2 className="profile-title">Profile</h2>
            <Link to="/settings">
              <SecondaryButton label="Profile Settings" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
          <div className="profile-layout">
              <div className="profile-avatar-wrapper">
                <AvatarUpload 
                  src={userProfile?.avatar || thumbPlaceholder} 
                  editable={false} 
                />
              </div>
              <div className="fields-grid profile-fields">
                <CustomInput label="First Name" name="firstName" value={userProfile?.first_name || ""} placeholder="e.g. Alex" onChange={() => {}} className="white-bg" disabled />
                <CustomInput label="Last Name" name="lastName" value={userProfile?.last_name || ""} placeholder="e.g. Johnson" onChange={() => {}} className="white-bg" disabled />
                <CustomInput label="Email" name="email" value={userProfile?.email || ""} onChange={() => {}} className="white-bg" disabled />
                <CustomInput label="Phone" name="phone" value={userProfile?.mobile || ""} onChange={() => {}} className="white-bg" disabled />
                <CustomInput label="Zip Code" name="zipCode" value={userProfile?.address_details?.zipcode || userProfile?.address?.zipcode || userProfile?.zipcode || ""} onChange={() => {}} className="white-bg" disabled />
                <CustomInput label="Address" name="address" value={typeof userProfile?.address === 'string' ? userProfile?.address : userProfile?.address_details?.address || userProfile?.address?.address || ""} onChange={() => {}} className="white-bg" disabled />
              </div>
            </div>
        </div>

        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Points Activity</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span className="wallet-section-header__note">Appears within 24 hrs</span>
            <Link to="/points-activity">
              <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
        </div>

        {loadingTransactions ? (
          <DataTable columns={activityColumns(handleViewActivity)} rows={[]} getRowKey={(row) => row.id} loading={true} />
        ) : transactions && transactions.length > 0 ? (
          <DataTable 
            columns={activityColumns(handleViewActivity)} 
            rows={transactions.slice(0, 10)} 
            getRowKey={(row) => row.id}
          />
        ) : (
          <EmptyState
            title="No recent activity yet"
            message="Your recent dining activity and transactions will appear here."
          />
        )}
      </Container>

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

ProfilePage.propTypes = {
  profile: PropTypes.shape({
    photoUrl: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    zipCode: PropTypes.string,
    address: PropTypes.string,
  }).isRequired,
  activity: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      category: PropTypes.oneOf(["dining", "points", "voucher", "game"]).isRequired,
      activity: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      points: PropTypes.number,
      date: PropTypes.string.isRequired,
    })
  ).isRequired,
  onOpenSettings: PropTypes.func,
  onViewActivity: PropTypes.func,
};

ProfilePage.defaultProps = {
  onOpenSettings: () => {},
  profile: {
    photoUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80",
    firstName: "Alex",
    lastName: "Johnson",
    email: "alex.johnson@example.com",
    phone: "+1 (555) 123-4567",
    zipCode: "68102",
    address: "123 Main Street, Omaha, NE",
  },
  activity: [
    {
      id: 1,
      category: "dining",
      activity: "Dined at Pitch Pizzeria",
      status: "completed",
      type: "Dining",
      points: 25,
      date: "Today",
     
    },
    {
      id: 2,
      category: "points",
      activity: "Earned points from referral",
      status: "completed",
      type: "Referral",
      points: 100,
      date: "Yesterday",
    },
    {
      id: 3,
      category: "voucher",
      activity: "Redeemed $25 voucher",
      status: "redeemed",
      type: "Voucher",
      points: -2500,
      date: "2 days ago",
    },
    {
      id: 4,
      category: "game",
      activity: "Completed daily challenge",
      status: "completed",
      type: "Game",
      points: 50,
      date: "3 days ago",
    },
  ],
};

export default ProfilePage;
